import { createFileRoute, Link } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useMemo, useState } from "react";
import { toast } from "sonner";

import {
  createAdminImageUpload,
  deleteAdminStorageObjects,
  listAdminPlaces,
  updatePlaceImages,
} from "@/lib/admin.functions";
import { supabase } from "@/integrations/supabase/client";
import { compressImage, MAX_IMAGE_BYTES } from "@/lib/compress-image";

export const Route = createFileRoute("/_authenticated/admin/compress-images")({
  component: CompressImagesPage,
});

const PUBLIC_PREFIX = "/storage/v1/object/public/content-images/";

// Extract the object path inside the content-images bucket, or null if the URL
// points somewhere else (e.g. Unsplash, external CDN — we skip those).
function extractContentImagePath(url: string): string | null {
  try {
    const u = new URL(url);
    const idx = u.pathname.indexOf(PUBLIC_PREFIX);
    if (idx === -1) return null;
    return decodeURIComponent(u.pathname.slice(idx + PUBLIC_PREFIX.length));
  } catch {
    return null;
  }
}

// Derive folder + slug from an existing storage path like "places/tampere/1699-abc.jpg"
function parsePath(path: string): { folder: string; slug: string } {
  const parts = path.split("/");
  return {
    folder: parts[0] || "places",
    slug: parts[1] || "misc",
  };
}

type Row = {
  id: string;
  name: string;
  city_slug: string;
  image_url: string | null;
  gallery_urls: string[] | null;
  status: "pending" | "running" | "done" | "skipped" | "error";
  note: string;
};

function CompressImagesPage() {
  const listFn = useServerFn(listAdminPlaces);
  const createUploadFn = useServerFn(createAdminImageUpload);
  const updateFn = useServerFn(updatePlaceImages);
  const deleteFn = useServerFn(deleteAdminStorageObjects);

  const [rows, setRows] = useState<Row[]>([]);
  const [loading, setLoading] = useState(false);
  const [running, setRunning] = useState(false);
  const [progress, setProgress] = useState({ done: 0, total: 0 });

  async function load() {
    setLoading(true);
    try {
      const { places } = await listFn();
      const filtered = (places as any[])
        .filter((p) => p.image_url || (p.gallery_urls && p.gallery_urls.length))
        .map<Row>((p) => ({
          id: p.id,
          name: p.name,
          city_slug: p.city_slug,
          image_url: p.image_url,
          gallery_urls: p.gallery_urls ?? null,
          status: "pending",
          note: "",
        }));
      setRows(filtered);
      toast.success(`Loaded ${filtered.length} places with images`);
    } catch (e: any) {
      toast.error(e?.message ?? "Failed to load places");
    } finally {
      setLoading(false);
    }
  }

  // Fetch a public image URL and hand it to the shared compressImage helper.
  // Returns null when the URL isn't a bucket object or is already <= 150KB.
  async function recompressOne(url: string): Promise<{ newUrl: string; oldPath: string } | null> {
    const oldPath = extractContentImagePath(url);
    if (!oldPath) return null; // external URL — leave alone

    const res = await fetch(url, { cache: "no-store" });
    if (!res.ok) throw new Error(`fetch failed (${res.status})`);
    const blob = await res.blob();
    if (blob.size <= MAX_IMAGE_BYTES) return null; // already small enough

    const nameFromPath = oldPath.split("/").pop() || "image";
    const file = new File([blob], nameFromPath, { type: blob.type });
    const compressed = await compressImage(file);

    const { folder, slug } = parsePath(oldPath);
    const ext = (compressed.name.split(".").pop() || "jpg").toLowerCase();
    const upload = await createUploadFn({
      data: {
        folder: folder as any,
        slug,
        ext: ext as any,
        contentType: compressed.type as any,
      },
    });
    const { error } = await supabase.storage
      .from("content-images")
      .uploadToSignedUrl(upload.path, upload.token, compressed, {
        cacheControl: "3600",
        contentType: compressed.type,
      });
    if (error) throw new Error(error.message);
    return { newUrl: upload.publicUrl, oldPath };
  }

  async function processRow(row: Row) {
    setRows((prev) => prev.map((r) => (r.id === row.id ? { ...r, status: "running", note: "…" } : r)));
    try {
      let newImage: string | null | undefined;
      let newGallery: string[] | undefined;
      const toDelete: string[] = [];
      let changed = 0;

      if (row.image_url) {
        const r = await recompressOne(row.image_url);
        if (r) {
          newImage = r.newUrl;
          toDelete.push(r.oldPath);
          changed++;
        }
      }

      if (row.gallery_urls && row.gallery_urls.length) {
        const next: string[] = [];
        for (const g of row.gallery_urls) {
          try {
            const r = await recompressOne(g);
            if (r) {
              next.push(r.newUrl);
              toDelete.push(r.oldPath);
              changed++;
            } else {
              next.push(g);
            }
          } catch {
            next.push(g);
          }
        }
        newGallery = next;
      }

      if (changed === 0) {
        setRows((prev) => prev.map((r) => (r.id === row.id ? { ...r, status: "skipped", note: "already ≤150KB" } : r)));
        return;
      }

      await updateFn({
        data: {
          id: row.id,
          ...(newImage !== undefined ? { image_url: newImage } : {}),
          ...(newGallery !== undefined ? { gallery_urls: newGallery } : {}),
        },
      });

      // Delete old originals in chunks of 50 (server limit).
      for (let i = 0; i < toDelete.length; i += 50) {
        await deleteFn({ data: { paths: toDelete.slice(i, i + 50) } });
      }

      setRows((prev) =>
        prev.map((r) => (r.id === row.id ? { ...r, status: "done", note: `compressed ${changed} image(s)` } : r)),
      );
    } catch (e: any) {
      setRows((prev) => prev.map((r) => (r.id === row.id ? { ...r, status: "error", note: e?.message ?? "failed" } : r)));
    }
  }

  async function runAll() {
    setRunning(true);
    const targets = rows.filter((r) => r.status === "pending" || r.status === "error");
    setProgress({ done: 0, total: targets.length });
    for (let i = 0; i < targets.length; i++) {
      await processRow(targets[i]);
      setProgress({ done: i + 1, total: targets.length });
    }
    setRunning(false);
    toast.success("Backfill complete");
  }

  const summary = useMemo(() => {
    const c = { done: 0, skipped: 0, error: 0, pending: 0, running: 0 };
    for (const r of rows) c[r.status]++;
    return c;
  }, [rows]);

  return (
    <div className="max-w-6xl mx-auto px-4 py-6">
      <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
        <div>
          <h1 className="text-xl font-bold text-navy">Compress Place Images</h1>
          <p className="text-sm text-navy/60">
            Recompress every image in the places table to ≤150 KB, then delete the original high-quality file.
            External URLs (Unsplash etc.) are skipped.
          </p>
        </div>
        <Link to="/admin" className="text-sm text-navy/70 underline">← back to admin</Link>
      </div>

      <div className="flex items-center gap-2 mb-4">
        <button
          onClick={load}
          disabled={loading || running}
          className="px-3 py-2 text-sm font-semibold rounded border bg-white hover:bg-slate-50 disabled:opacity-50"
        >
          {loading ? "loading…" : "1. Load places"}
        </button>
        <button
          onClick={runAll}
          disabled={running || rows.length === 0}
          className="px-3 py-2 text-sm font-semibold rounded bg-navy text-white disabled:opacity-50"
        >
          {running ? `running… ${progress.done}/${progress.total}` : `2. Compress ${rows.length} places`}
        </button>
        {rows.length > 0 && (
          <div className="text-xs text-navy/70 ml-2">
            done {summary.done} · skipped {summary.skipped} · error {summary.error} · pending {summary.pending}
          </div>
        )}
      </div>

      <div className="border rounded overflow-hidden overflow-x-auto">
        <table className="w-full text-sm min-w-[640px]">

          <thead className="bg-slate-100 text-left">
            <tr>
              <th className="p-2">Place</th>
              <th className="p-2">City</th>
              <th className="p-2">Images</th>
              <th className="p-2">Status</th>
              <th className="p-2">Note</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id} className="border-t">
                <td className="p-2 font-medium">{r.name}</td>
                <td className="p-2">{r.city_slug}</td>
                <td className="p-2">
                  {(r.image_url ? 1 : 0) + (r.gallery_urls?.length ?? 0)}
                </td>
                <td className="p-2">
                  <span
                    className={
                      r.status === "done"
                        ? "text-green-700"
                        : r.status === "error"
                        ? "text-red-700"
                        : r.status === "running"
                        ? "text-blue-700"
                        : r.status === "skipped"
                        ? "text-slate-500"
                        : "text-navy/70"
                    }
                  >
                    {r.status}
                  </span>
                </td>
                <td className="p-2 text-xs text-navy/60">{r.note}</td>
              </tr>
            ))}
            {rows.length === 0 && (
              <tr>
                <td colSpan={5} className="p-4 text-center text-navy/50">
                  No places loaded yet.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
